import { useCallback, useEffect, useState } from "react";
import { Link, matchPath, useLocation, useNavigate } from "react-router-dom";

/**Import Assets */
import styles from "./styles/ProtectedLayout.module.scss";
// import cssStyles from './styles/notification.module.css';

/**Import Custom Component */
import { WEB_ROUTES } from "../../constants";
import { baseRoute } from "../../Helper";
import Navbar from "./Navbar";

// Icons
import { FaUser } from "react-icons/fa";
import { IoCarSportSharp, IoNotifications } from "react-icons/io5";
import {
  MdBookOnline,
  MdDashboard,
  MdPolicy,
  MdSwapHoriz,
} from "react-icons/md";
import { removeToken } from "../../Data/services/localStorageService";
import { useLogoutUserMutation } from "../../Data/services/userAuthApi";
import Toast from "../../Hooks/useToast";
import LogoutModal from "../Modals/LogoutModal";

const items = [
  {
    label: "Dashboard",
    icon: <MdDashboard />,
    route: WEB_ROUTES.DASHBOARD,
    // lineBreak: true,
  },
  {
    label: "User Management",
    icon: <FaUser />,
    route: WEB_ROUTES.USER_MANAGEMENT,
    lineBreak: true,
  },
  {
    label: "Vehicle Management",
    icon: <IoCarSportSharp />,
    route: WEB_ROUTES.VEHICLE_MANAGEMENT,
  },
  {
    label: "Booking",
    icon: <MdBookOnline />,
    route: WEB_ROUTES.BOOKING_MANAGEMENT,
  },
  {
    label: "Transaction",
    icon: <MdSwapHoriz />,
    route: WEB_ROUTES.TRANSACTION_MANAGEMENT,
    lineBreak: true,
  },
  {
    label: "Send Notification",
    icon: <IoNotifications />,
    route: WEB_ROUTES.SEND_NOTIFICATION,
  },
  // {
  //   label: "Settings",
  //   icon: <IoIosSettings />,
  //   route: WEB_ROUTES.SETTING,
  // },
  {
    label: "Page Management",
    icon: <MdPolicy />,
    route: WEB_ROUTES.PAGE_MANAGEMENT,
  },
];

export default function ProtectedLayout({
  isSearchBar,
  isSettingButton,
  selectedRoute,
  children,
}) {
  const [collapsed, setCollapsed] = useState(window.innerWidth > 991);
  const location = useLocation();
  const navigate = useNavigate();
  const [logoutModal, setLogoutModal] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 991) {
        setCollapsed(false);
      } else {
        setCollapsed(true);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isActiveRoute = useCallback(
    (route) => {
      return matchPath(location.pathname, route) !== null;
    },
    [location.pathname]
  );

  const handleSettings = useCallback(() => {
    navigate(WEB_ROUTES.SETTING);
  }, [navigate]);

  const handleToggle = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  const handleMenuClose = useCallback(() => {
    setCollapsed(false);
  }, []);

  const handleViewProfile = useCallback(() => {
    navigate(baseRoute(WEB_ROUTES.PROFILE));
  }, [navigate]);

  const handleLogoutModalOpen = useCallback(() => {
    setLogoutModal(true);
  }, []);

  const handleLogoutModalClose = useCallback(() => {
    setLogoutModal(false);
  }, []);

  const [logoutUser, { isLoading }] = useLogoutUserMutation();

  const handleLogout = useCallback(async () => {
    try {
      const response = await logoutUser().unwrap();
      if (response.code === 200) {
        removeToken();
        navigate("/account");
        window.location.reload();
        Toast("Logged Out Successfully", "success");
      }
    } catch {
      Toast("An error occurred during log out", "error");
    }
  }, [logoutUser, navigate]);

  return (
    <div className={styles.ProtectedLayout}>
      <div
        className={collapsed ? styles.background : ""}
        onClick={handleMenuClose}
      ></div>
      <div
        className={`${styles.menu} ${
          collapsed ? `${styles.collapsed} visible` : styles.notCollapsed
        }`}
        id="sider"
      >
        <div
          className={`${styles.logo} ${
            !collapsed ? styles.notCollapsed : ""
          } rounded-circle`}
          to={`${process.env.REACT_APP_DOMAIN_DIR}/dashboard`}
        >
          <img
            src={collapsed ? "/images/logo_name.png" : "/images/small_logo.png"} //todo:
            alt="Logo"
            className={`${
              collapsed ? styles.imageCollapsed : styles.imageNotCollapsed
            } image-fluid object-fit-contain`}
            width={"100%"}
            height={"100%"}
          />
        </div>
        {items.map(({ icon, label, route, lineBreak }) => {
          return (
            <div key={route}>
              <Link
                to={route}
                className={`${styles.item} ${
                  isActiveRoute(route) ? styles.active : ""
                } ${!collapsed ? styles.collapsed : ""}`}
              >
                <span style={{ fontSize: "20px", display: "flex" }}>
                  {icon}
                </span>
                {collapsed ? <p>{label}</p> : ""}
              </Link>
              {lineBreak ? <hr className="w-100 m-0"></hr> : null}
            </div>
          );
        })}
      </div>
      <div className={styles.routeContent}>
        {logoutModal ? (
          <LogoutModal
            handleLogoutModalClose={handleLogoutModalClose}
            data={""}
            loading={isLoading}
            onLogout={handleLogout}
          />
        ) : null}
        <Navbar
          isSearchBar={isSearchBar}
          isSettingButton={isSettingButton}
          handleToggle={handleToggle}
          handleLogoutModalOpen={handleLogoutModalOpen}
          handleSettings={handleSettings}
        />
        <div className={styles.content}>{children}</div>
      </div>
    </div>
  );
}
